import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, InfoBox, LoadingState, Badge } from '@biznomi/ui-react'
import { resolveDeviceIdentity, unavailableMessage, getDeviceName, type DeviceIdentity } from '@/lib/device'
import { getMyDevices, registerDevice, cancelPendingDevice } from '@/lib/api/device'

/**
 * 기기 등록 — 태깅 전에 반드시 통과해야 하는 관문.
 *
 * 여기서 가장 중요한 것은 **기기를 확인하지 못했을 때 아무것도 만들어내지 않는 것**이다.
 * 임의 ID 로 등록을 시도하면 그게 그대로 새 기기가 되고, 한 계정에 두 기기가
 * 살아나면 대리출석이 가능해진다 (스펙 §5-4).
 */
export default function DeviceRegister() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [identity, setIdentity] = useState<DeviceIdentity | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    resolveDeviceIdentity().then((r) => alive && setIdentity(r))
    return () => {
      alive = false
    }
  }, [])

  const { data: devices, isLoading } = useQuery({
    queryKey: ['my-devices'],
    queryFn: async () => {
      const res = await getMyDevices()
      if (!res.success) throw new Error(res.error ?? '기기 목록을 불러오지 못했습니다')
      return res.data ?? []
    },
  })

  const approved = devices?.find((d) => d.status === 'APPROVED' && d.isActive)
  const pending = devices?.find((d) => d.status === 'PENDING')
  const alreadyRegistered =
    identity?.source !== 'unavailable' &&
    identity != null &&
    devices?.some((d) => d.deviceId === identity.id)

  async function submit() {
    if (!identity || identity.source === 'unavailable') return
    setSubmitting(true)
    setError('')
    const res = await registerDevice(identity)
    setSubmitting(false)
    if (!res.success) {
      setError(res.error ?? '등록 요청에 실패했습니다')
      return
    }
    qc.invalidateQueries({ queryKey: ['my-devices'] })
  }

  async function cancel(id: string) {
    await cancelPendingDevice(id)
    qc.invalidateQueries({ queryKey: ['my-devices'] })
  }

  if (identity === null || isLoading) return <LoadingState full label="기기를 확인하는 중…" />

  // ── 기기를 확인하지 못한 경우 — 임의 ID 로 우회하지 않는다 ──
  if (identity.source === 'unavailable') {
    return (
      <div className="space-y-5">
        <header>
          <h1 className="text-xl font-bold">기기를 확인하지 못했습니다</h1>
        </header>
        <InfoBox tone="warning">{unavailableMessage(identity.reason)}</InfoBox>
        <p className="text-xs leading-relaxed text-slate-400">
          기기를 확인하지 못한 상태에서는 등록을 진행하지 않습니다. 임의로 등록하면 다른 기기가 내 계정으로
          출퇴근을 찍을 수 있기 때문입니다.
        </p>
        <Button size="lg" className="w-full" onClick={() => window.location.reload()}>
          다시 시도
        </Button>
      </div>
    )
  }

  // ── 이미 승인된 기기 ──
  if (approved && approved.deviceId === identity.id) {
    return (
      <div className="space-y-5">
        <header>
          <h1 className="text-xl font-bold">등록된 기기입니다</h1>
          <p className="mt-1 text-sm text-slate-500">{approved.deviceName ?? '내 기기'}</p>
        </header>
        <InfoBox tone="success">이 기기로 출퇴근을 기록할 수 있습니다.</InfoBox>
        <Button size="lg" className="w-full" onClick={() => navigate('/', { replace: true })}>
          시작하기
        </Button>
      </div>
    )
  }

  // ── 승인 대기 ──
  if (pending && pending.deviceId === identity.id) {
    return (
      <div className="space-y-5">
        <header className="flex items-center gap-2">
          <h1 className="text-xl font-bold">승인 대기 중</h1>
          <Badge variant="warning">대기</Badge>
        </header>
        <InfoBox tone="info">
          관리자가 승인하면 이 기기로 출퇴근을 기록할 수 있습니다. 승인 전까지는 태깅이 되지 않습니다.
        </InfoBox>
        <Button variant="secondary" size="lg" className="w-full" onClick={() => cancel(pending.id)}>
          요청 취소
        </Button>
      </div>
    )
  }

  // ── 등록 요청 ──
  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-bold">기기 등록</h1>
        <p className="mt-1 text-sm text-slate-500">
          출퇴근은 등록된 기기에서만 기록됩니다. 대리 출석을 막기 위한 절차입니다.
        </p>
      </header>

      <dl className="divide-y divide-slate-100 overflow-hidden rounded-card bg-card">
        <div className="flex items-center justify-between px-4 py-3.5">
          <dt className="text-sm text-slate-500">기기</dt>
          {/*
            🔴 라벨은 「기기」인데 값이 **접속 방식**이었다 (2026-09-12 리허설).
               등록 전에는 「웹 브라우저」, 등록 뒤에는 「Mac」 이 떠서 같은 자리가
               두 번 다르게 보였다. 관리자 목록도 deviceName(=Mac)을 쓴다.
               둘 다 사실이므로 둘 다 적는다 — **이름이 앞이다.** 등록 뒤 화면과
               관리자 화면이 그 이름으로 이어진다.
          */}
          <dd className="text-sm font-medium">
            {getDeviceName() || '이 기기'} · {identity.source === 'native' ? '앱' : '웹 브라우저'}
          </dd>
        </div>
        {identity.source === 'browser' && (
          <div className="px-4 py-3">
            <p className="text-xs leading-relaxed text-slate-400">
              웹 브라우저는 기기에 고정되지 않아 저장소를 지우면 다시 등록해야 합니다. 앱을 쓰면 그런 일이
              없습니다.
            </p>
          </div>
        )}
      </dl>

      {approved && (
        <InfoBox tone="warning">
          이미 다른 기기({approved.deviceName ?? '기기'})가 등록돼 있습니다. 새 기기는 관리자 승인이
          필요합니다.
        </InfoBox>
      )}

      {alreadyRegistered && !pending && !approved && (
        <InfoBox tone="info">이전에 요청한 기록이 있습니다. 관리자에게 문의해 주세요.</InfoBox>
      )}

      {error && (
        <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      <Button size="lg" className="w-full" loading={submitting} onClick={submit}>
        이 기기 등록
      </Button>
    </div>
  )
}
